import type { AccountBundleV1, Argon2idParametersV1, CryptoProfileCreateV1, WrappedKeyBundleV1 } from './types';
import { ARGON2ID_PARAMETERS } from './types';
import { aesGcmDecrypt, aesGcmEncrypt, assertBundle, bundleBytes, createAccountBundle, derivePassphraseKey, fingerprint, randomBytes } from './primitives';
import { base64url, fromUtf8, unbase64url, utf8 } from './encoding';
import { decodeRecoveryKey, generateRecoveryKey } from './recovery';

function wrapAad(userID: string, keyRevision: number, wrapType: 'passphrase' | 'recovery') {
  return utf8(`holdthat:key-bundle:v1:${userID}:${keyRevision}:${wrapType}`);
}

export async function wrapWithPassphrase(bundle: AccountBundleV1, userID: string, passphrase: string): Promise<WrappedKeyBundleV1> {
  const parameters: Argon2idParametersV1 = { ...ARGON2ID_PARAMETERS, salt: base64url(randomBytes(16)) };
  const key = await derivePassphraseKey(passphrase, parameters);
  const sealed = await aesGcmEncrypt(key, bundleBytes(bundle), wrapAad(userID, bundle.keyRevision, 'passphrase'));
  return { cryptoVersion: 1, keyRevision: bundle.keyRevision, wrapType: 'passphrase', kdf: parameters, nonce: base64url(sealed.nonce), ciphertext: base64url(sealed.ciphertext) };
}

export async function unwrapWithPassphrase(wrapped: WrappedKeyBundleV1, userID: string, passphrase: string): Promise<AccountBundleV1> {
  if (wrapped.wrapType !== 'passphrase' || !wrapped.kdf) throw new Error('Kein Passphrase-Bundle.');
  try {
    const key = await derivePassphraseKey(passphrase, wrapped.kdf);
    const plaintext = await aesGcmDecrypt(key, unbase64url(wrapped.nonce, 12), unbase64url(wrapped.ciphertext), wrapAad(userID, wrapped.keyRevision, 'passphrase'));
    return assertBundle(JSON.parse(fromUtf8(plaintext)) as AccountBundleV1);
  } catch {
    throw new Error('Die E2EE-Passphrase ist falsch oder das Schlüsselbund ist beschädigt.');
  }
}

export async function wrapWithRecoveryKey(bundle: AccountBundleV1, userID: string, recoveryKey: string): Promise<WrappedKeyBundleV1> {
  const sealed = await aesGcmEncrypt(await decodeRecoveryKey(recoveryKey), bundleBytes(bundle), wrapAad(userID, bundle.keyRevision, 'recovery'));
  return { cryptoVersion: 1, keyRevision: bundle.keyRevision, wrapType: 'recovery', nonce: base64url(sealed.nonce), ciphertext: base64url(sealed.ciphertext) };
}

export async function unwrapWithRecoveryKey(wrapped: WrappedKeyBundleV1, userID: string, recoveryKey: string): Promise<AccountBundleV1> {
  if (wrapped.wrapType !== 'recovery' || wrapped.kdf) throw new Error('Kein Recovery-Bundle.');
  const plaintext = await aesGcmDecrypt(await decodeRecoveryKey(recoveryKey), unbase64url(wrapped.nonce, 12), unbase64url(wrapped.ciphertext), wrapAad(userID, wrapped.keyRevision, 'recovery'));
  return assertBundle(JSON.parse(fromUtf8(plaintext)) as AccountBundleV1);
}

export async function createCryptoProfile(userID: string, passphrase: string, keyRevision = 1) {
  const bundle = await createAccountBundle(keyRevision); const recoveryKey = await generateRecoveryKey();
  const profile: CryptoProfileCreateV1 = {
    cryptoVersion: 1, keyRevision,
    encryptionPublicKey: bundle.encryptionPublicKey, signingPublicKey: bundle.signingPublicKey,
    fingerprint: await fingerprint(bundle.encryptionPublicKey, bundle.signingPublicKey),
    passphraseBundle: await wrapWithPassphrase(bundle, userID, passphrase),
    recoveryBundle: await wrapWithRecoveryKey(bundle, userID, recoveryKey),
  };
  return { bundle, recoveryKey, profile };
}
